// Règle UNIQUE de recherche de société — incident SNT MORENO (26/09/2026).
//
// Treize sélecteurs cherchaient chacun à leur façon, presque tous par
// `nom.toLowerCase().includes(saisie)` : « SNT » ne trouvait pas
// « SOCIETE NOUVELLE DES TRANSPORTS MORENO (S.N.T MORENO) » (points), « chateau » ne trouvait
// pas « Châteaux des Bondons » (accent), « saint soupplets » ne trouvait pas
// « CCAS de Saint-Soupplets » (tiret), et le nom court, les alias et le préfixe n'étaient
// jamais lus. Tout sélecteur de société passe désormais par ce module
// (garde-fou : rechercheSociete.guard.test.js).
//
// Miroir serveur : backend/app/services/recherche_societe_svc.py (même clé, même règle mot à mot).

// Clé de comparaison : accents retirés, minuscules, tout ce qui n'est ni lettre ni chiffre
// supprimé. « S.N.T » → « snt » ; « Châteaux » → « chateaux » ; « Saint-Soupplets » →
// « saintsoupplets ». La décomposition NFD sépare l'accent de sa lettre (« â » → « a » +
// accent combinant) ; le filtre final, qui ne garde que [a-z0-9], retire donc l'accent.
export function cleRecherche(texte) {
  return String(texte ?? '')
    .normalize('NFD')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

// Mots de la saisie, chacun réduit à sa clé. Un « mot » fait seulement de ponctuation tombe.
export function motsRecherche(saisie) {
  return String(saisie ?? '').split(/\s+/).map(cleRecherche).filter(Boolean);
}

// Champs lus par défaut. `aliases` est une liste ; tous peuvent être `null`, "", `[]` ou
// absents (les trois formes existent en base).
export const CHAMPS_SOCIETE = ['name', 'short_name', 'aliases', 'notification_prefix'];

function clesSociete(societe, champsEnPlus) {
  const cles = [];
  for (const champ of [...CHAMPS_SOCIETE, ...champsEnPlus]) {
    const valeur = societe ? societe[champ] : undefined;
    for (const v of (Array.isArray(valeur) ? valeur : [valeur])) {
      const cle = cleRecherche(v);
      if (cle) cles.push(cle);
    }
  }
  return cles;
}

// Saisie NON vide faite seulement de ponctuation (« -- », « & ») : elle ne trouve AUCUNE
// société — ni toutes. Même règle que toutes les routes du serveur (relecture du 26/09/2026).
export function saisieSansMot(saisie) {
  return String(saisie ?? '').trim() !== '' && !motsRecherche(saisie).length;
}

// Vrai si CHAQUE mot de la saisie est contenu dans l'une des clés de la société (nom, nom
// court, un alias, préfixe, et les champs en plus). Saisie vide → tout correspond ;
// ponctuation seule → rien.
export function societeCorrespond(societe, saisie, champsEnPlus = []) {
  const mots = motsRecherche(saisie);
  if (!mots.length) return !saisieSansMot(saisie);
  const cles = clesSociete(societe, champsEnPlus);
  return mots.every(mot => cles.some(cle => cle.includes(mot)));
}

export function filtrerSocietes(liste, saisie, champsEnPlus = []) {
  const societes = Array.isArray(liste) ? liste : [];
  if (saisieSansMot(saisie)) return [];
  if (!motsRecherche(saisie).length) return societes;
  return societes.filter(s => societeCorrespond(s, saisie, champsEnPlus));
}

// Une recherche ne masque JAMAIS un résultat : la limite d'affichage ne vaut que pour la
// liste sans saisie, et ce qu'elle cache s'annonce (« +N — affinez »).
export function limiterSansSaisie(liste, saisie, limite) {
  if (motsRecherche(saisie).length) return { visibles: liste, masques: 0 };
  return { visibles: liste.slice(0, limite), masques: Math.max(0, liste.length - limite) };
}

// Options de sélecteur générique ({ value, label }) qui portent leur société : les
// composants génériques cherchent alors l'option par la règle ci-dessus, et plus par son
// seul libellé.
export function optionsSocietes(societes) {
  return (societes || []).map(c => ({ value: c.id, label: c.name, societe: c }));
}

// Filtre d'options mixte : option portant une société → règle société ; autre option →
// `parDefaut(option, saisie)`, le filtre historique du composant (inchangé).
export function filtrerOptions(options, saisie, parDefaut) {
  const liste = options || [];
  if (!String(saisie ?? '').trim()) return liste;
  return liste.filter(o => (o && o.societe ? societeCorrespond(o.societe, saisie) : parDefaut(o, saisie)));
}

// Limite d'un composant générique : sans limite quand on cherche parmi des sociétés, la
// limite historique sinon (une liste de 2 500 contacts, elle, reste bornée).
export function limiterOptions(options, saisie, limite) {
  if ((options || []).some(o => o && o.societe)) return limiterSansSaisie(options, saisie, limite);
  return { visibles: options.slice(0, limite), masques: Math.max(0, options.length - limite) };
}

// Création depuis l'annuaire officiel (décision William, 26/09/2026) : le NOM reste le
// texte tapé… quand c'est un NOM. Le même champ sert de saisie à l'annuaire, qui accepte un
// SIREN : sans ce tri, « 813990603 » ou « transp mor » devenaient le nom de la société et
// partaient tels quels vers Odoo (relecture du 26/09/2026). Le nom officiel est repris quand :
//  - le texte tapé n'a que des chiffres (SIREN, SIRET ; espaces et points tolérés) ;
//  - l'un de ses mots n'est que le DÉBUT d'un mot du nom officiel (« transp », « mor ») :
//    c'est une recherche en cours de frappe, pas une appellation.
// Sinon (« SNT », « SNT MORENO », « Transports Moreno », un nom sans rapport) : le texte tapé.
function motsDuNom(texte) {
  const brut = String(texte ?? '');
  // Deux découpes : aux espaces (« S.N.T » → « snt ») et à toute ponctuation
  // (« SAINT-SOUPPLETS » → « saint », « soupplets »).
  return [...brut.split(/\s+/), ...brut.split(/[^0-9A-Za-zÀ-ɏḀ-ỿ]+/)]
    .map(cleRecherche).filter(Boolean);
}

export function nomDepuisAnnuaire(texteTape, resultat) {
  const r = resultat || {};
  const officiel = String(r.name || r.raison_sociale || '').trim();
  const tape = String(texteTape ?? '').trim();
  if (!tape) return officiel;
  const cle = cleRecherche(tape);
  if (!cle || /^[0-9]+$/.test(cle)) return officiel || tape;
  const motsOfficiels = new Set([r.name, r.raison_sociale, r.sigle].flatMap(motsDuNom));
  const debutSeulement = motsRecherche(tape).some(mot => !motsOfficiels.has(mot)
    && [...motsOfficiels].some(motOfficiel => motOfficiel.startsWith(mot)));
  return debutSeulement && officiel ? officiel : tape;
}

// La raison sociale officielle (aucun champ dédié n'existe) et le sigle vont en ALIAS, pour
// que la société reste trouvable sous chacun de ses noms. Une appellation déjà connue (même
// clé que le nom ou qu'un alias existant) n'est pas répétée.
export function aliasDepuisAnnuaire(nomTape, resultat, aliasExistants = []) {
  const vus = new Set([cleRecherche(nomTape)]);
  const aliases = [];
  for (const a of [...(aliasExistants || []), resultat && (resultat.raison_sociale || resultat.name), resultat && resultat.sigle]) {
    const texte = String(a ?? '').trim();
    const cle = cleRecherche(texte);
    if (!cle || vus.has(cle)) continue;
    vus.add(cle);
    aliases.push(texte);
  }
  return aliases;
}
